/**
 * Rate bookkeeping for `GET /api/auth/me`, plus the socket-reconnect cue that
 * triggers it, shared by every `AppShell` mount (FLOW-984).
 *
 * `/api/auth/me` is served under the backend's strict per-IP *auth* rate limit
 * (`rate_limit_auth_per_ip_per_min`, default 10/min) rather than the looser
 * authenticated-subtree limit, so the shell has a very small budget for it.
 * Two separate shell behaviours used to spend that budget without bound: the
 * auth-gate fetch on mount, and the re-fetch on every WebSocket reconnect.
 *
 * This state lives at module scope, not in the `AppShell` closure, because a
 * mount is far shorter-lived than the windows it has to enforce: every shell
 * route in `App.tsx` instantiates its own `<AppShell>`, and `@solidjs/router`
 * keys each route's reactive root on the route definition object (`key:
 * routeDef` in its `routeStates` memo), disposing the old root and creating a
 * fresh one on each client-side navigation. Per-instance state would reset on
 * every nav click and neither bound would hold.
 *
 * Module scope is still per-tab, while the server's budget is per-IP, so the
 * two bounds below cap a tab at ~2 calls per window and not a client: enough
 * tabs, or a shared egress IP, can still reach the limit. Bounding it across
 * tabs would need a `BroadcastChannel` or a `localStorage` timestamp, which is
 * not worth it for a budget this far from the observed failure.
 *
 * The two bounds are tracked separately on purpose. Priming the reconnect
 * throttle with the gate's fetch would delay the role/status re-fetch that a
 * reconnect exists to trigger (FLOW-663) by up to a full window, and that
 * re-fetch is what carries the two user-visible consequences of staleness:
 * the `/pending` redirect for an account that has just been suspended, and
 * the role-derived controls a demoted admin keeps seeing until it lands.
 *
 * Note the reconnect cue only fires for revocations that actually close the
 * socket — a tenant-role change, `ProjectService::remove_member`, and session
 * or PAT revocation all call `notify_ws_kick`. A plain status change does not:
 * `just block-user` writes `users.status` and `access_revoked_at` together
 * (FLOW-962), but neither column revokes the session or closes the open
 * socket, and the socket's periodic re-check does not re-derive
 * `users.status` either. A project-level role change likewise does not kick.
 * For those, the stale cache lasts until the next mount that finds the
 * window lapsed re-fetches. That is event-driven, not a time bound: nothing
 * polls `/api/auth/me` on a timer, so a tab that neither navigates nor sees
 * the socket flap can hold the stale chrome for the rest of its life, where
 * the pre-FLOW-984 code re-fetched on every navigation.
 *
 * All of it is presentation only. The server re-checks on every request —
 * `ActiveUser` rejects a non-active caller and `require_project_tenant_admin`
 * re-resolves the role — so a stale cache shows a control that then fails,
 * never one that succeeds.
 */

/** Minimum spacing between two `GET /api/auth/me` calls of the same kind. */
const USER_REFRESH_MIN_INTERVAL_MS = 60000;

/**
 * How long an in-flight auth-gate fetch may be shared before later mounts stop
 * adopting it. `getMe()` goes through `api/client`, which issues a bare
 * `fetch()` with no `AbortController`, so a stalled connection can hang
 * indefinitely; without this bound the shared slot would turn one hung request
 * into a permanently gated shell for every subsequent navigation in the tab,
 * which is strictly worse than the per-mount fetch it replaced. Retiring the
 * slot does not abort the hung request, it only stops it being handed out —
 * restoring exactly the fresh attempt each new mount used to get.
 */
const IN_FLIGHT_MAX_MS = 15000;

/** Any `/api/auth/me` the shell has issued; bounds the auth-gate fetch. */
let lastUserFetchAt = 0;
/** Only reconnect-driven refreshes; bounds the storm. */
let lastReconnectRefreshAt = 0;
let pendingRefresh: ReturnType<typeof setTimeout> | null = null;
/** The auth-gate fetch currently in flight, so concurrent mounts share it. */
let pendingFetch: Promise<boolean> | null = null;
let pendingFetchTimer: ReturnType<typeof setTimeout> | null = null;
/** Whether the socket has gone down since the last reconnect was acted on. */
let sawDisconnect = false;

/**
 * Records that an `/api/auth/me` request has just been *issued* — not that it
 * succeeded. A 429 or a network failure consumed the same budget a success
 * would have, so it has to re-arm the window too; counting only successes
 * would make a run of 429s retry on every mount and re-spend a budget that is
 * already exhausted, which is the bug this module exists to prevent. The cost
 * is that each failure re-arms the window, so a failure that persists longer
 * than one window — a shared-IP 429 that has not cleared, an offline client —
 * keeps re-validation to one attempt per window for as long as it lasts
 * rather than for one extra window. That is freshness only: a suspension or
 * demotion is reported as a 200 with the new `status`/`tenants`, never as a
 * failure, and the server re-authorizes every request regardless.
 */
export function markUserFetched(): void {
  lastUserFetchAt = Date.now();
}

/**
 * Whether the last `/api/auth/me` is recent enough that a newly mounted shell
 * can open its auth gate on the already-published user instead of re-fetching
 * one. This is what stops a client-side navigation from costing a request.
 */
export function isUserFetchFresh(): boolean {
  return Date.now() - lastUserFetchAt < USER_REFRESH_MIN_INTERVAL_MS;
}

/**
 * Shares one in-flight auth-gate fetch across every shell mount that asks for
 * one while it is still running.
 *
 * `isUserFetchFresh()` alone does not cover this: `markUserFetched()` stamps
 * the window *before* the request goes out, but the user is only published
 * once it comes back, so during that gap a new mount sees a fresh window and
 * no user — it fails the gate's `currentUser() && isUserFetchFresh()` and
 * issues a second request. The router disposes the previous shell's reactive
 * root on a navigation but does not cancel its in-flight promise, so mounts
 * can genuinely overlap. Unbounded, that is one request per mount for as long
 * as the first one takes.
 *
 * Followers adopt the leader's result, including its rejection: a caller that
 * would have made its own failing request sees the same failure, so the gate
 * still stays closed rather than opening on a fetch nobody completed. The slot
 * is cleared once the request settles, which leaves the steady-state bound to
 * `isUserFetchFresh()` exactly as before.
 *
 * The slot is a shortcut, never a lock: it is also retired after
 * `IN_FLIGHT_MAX_MS` so a request that never settles cannot gate every later
 * mount in the tab.
 *
 * Deliberately not applied to the reconnect refresh. That path exists to
 * observe state *after* a kick, and a gate fetch already in flight may have
 * been issued before the change it is meant to pick up; it keeps its own
 * window (`scheduleThrottledRefresh`) for the same reason the two bounds are
 * tracked separately.
 */
export function coalesceUserFetch(
  fetch: () => Promise<boolean>,
): Promise<boolean> {
  if (pendingFetch) return pendingFetch;
  // Retire only if the slot is still ours: `resetAuthRefreshThrottle()` may
  // have cleared it mid-flight for a new identity, and clobbering that would
  // let a later mount adopt the previous identity's request.
  const retire = (slot: Promise<boolean>) => {
    if (pendingFetch !== slot) return;
    pendingFetch = null;
    if (pendingFetchTimer) clearTimeout(pendingFetchTimer);
    pendingFetchTimer = null;
  };
  const inFlight = fetch().finally(() => retire(inFlight));
  pendingFetch = inFlight;
  pendingFetchTimer = setTimeout(() => retire(inFlight), IN_FLIGHT_MAX_MS);
  return inFlight;
}

/**
 * Records that the shared socket went down. Lives here, beside the windows,
 * rather than in the `AppShell` closure that observes it: the socket is
 * app-wide and outlives any one mount, but `useWebSocket` registers its
 * status effect with `defer: true`, so a shell that mounts while the socket
 * is already down is never told about it. With a per-instance flag, a
 * disconnect seen by the shell before a navigation and the reconnect seen by
 * the shell after it would cancel out and the FLOW-663 refresh would never be
 * scheduled at all — not deferred, lost.
 */
export function markSocketDisconnected(): void {
  sawDisconnect = true;
}

/**
 * Whether this `connected` transition is a *re*connect, clearing the cue so
 * one disconnect drives at most one refresh.
 */
export function takeSocketReconnect(): boolean {
  if (!sawDisconnect) return false;
  sawDisconnect = false;
  return true;
}

/**
 * Runs `refresh` now when no reconnect-driven refresh has happened inside the
 * window, otherwise defers it to the end of the window — deferred, never
 * dropped, because the role or status change that triggered it still has to
 * land. A refresh already deferred wins: it will observe the same latest
 * server state, so a second trigger inside the window needs no second call.
 */
export function scheduleThrottledRefresh(refresh: () => void): void {
  if (pendingRefresh) return;
  const sinceLast = Date.now() - lastReconnectRefreshAt;
  if (sinceLast >= USER_REFRESH_MIN_INTERVAL_MS) {
    lastReconnectRefreshAt = Date.now();
    refresh();
    return;
  }
  // Deliberately not cancelled when the triggering shell unmounts: a
  // navigation mid-window must not drop the pending refresh, and the only
  // ways out of the session (`logout()` and the `/pending` redirect) are full
  // page loads, so a late refresh cannot outlive the session either.
  pendingRefresh = setTimeout(() => {
    pendingRefresh = null;
    lastReconnectRefreshAt = Date.now();
    refresh();
  }, USER_REFRESH_MIN_INTERVAL_MS - sinceLast);
}

/**
 * Clears both windows, any deferred refresh, any in-flight auth-gate fetch,
 * and the cue. Called by `logout()` so a later identity in this tab cannot
 * inherit a half-spent window, an armed refresh, or a request issued for the
 * previous one, and by tests between cases.
 */
export function resetAuthRefreshThrottle(): void {
  if (pendingRefresh) clearTimeout(pendingRefresh);
  pendingRefresh = null;
  if (pendingFetchTimer) clearTimeout(pendingFetchTimer);
  pendingFetchTimer = null;
  pendingFetch = null;
  lastUserFetchAt = 0;
  lastReconnectRefreshAt = 0;
  sawDisconnect = false;
}
